import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UserProfile as Profile } from 'oidc-client-ts';

import type { RootState } from '../setup/store';
import { accessToken } from './accessToken';

export type AccessToken = string | undefined | null;

export const TENANT_RIO_EU_PROD = 'rio-eu.prod';
export const TENANT_RIO_EU_TEST = 'rio-eu.test';
export const TENANT_RIO_BRAZIL_PROD = 'rio-brazil.prod';
export const TENANT_RIO_BRAZIL_TEST = 'rio-brazil.test';
export const TENANT_RIO_SOUTHKOREA_PROD = 'rio-southkorea.prod';
export const TENANT_RIO_SOUTHKOREA_TEST = 'rio-southkorea.test';
export const TENANT_RIO_SOUTHAFRICA_PROD = 'rio-southafrica.prod';
export const TENANT_RIO_SOUTHAFRICA_TEST = 'rio-southafrica.test';

export type Tenant =
    | typeof TENANT_RIO_EU_PROD
    | typeof TENANT_RIO_EU_TEST
    | typeof TENANT_RIO_BRAZIL_PROD
    | typeof TENANT_RIO_BRAZIL_TEST
    | typeof TENANT_RIO_SOUTHKOREA_TEST
    | typeof TENANT_RIO_SOUTHKOREA_PROD
    | typeof TENANT_RIO_SOUTHAFRICA_TEST
    | typeof TENANT_RIO_SOUTHAFRICA_PROD;

export interface AccessTokenState {
    accessToken: AccessToken;
    idToken: Profile | null;
}

const initialState: AccessTokenState = {
    accessToken: accessToken.getAccessToken(),
    idToken: null,
};

const tokenSlice = createSlice({
    name: 'tokenHandling',
    initialState,
    reducers: {
        accessTokenStored: (state, action: PayloadAction<AccessToken>) => {
            state.accessToken = action.payload;
        },
        idTokenStored: (state, action: PayloadAction<Profile>) => {
            state.idToken = action.payload;
        },
    },
});

export const { accessTokenStored, idTokenStored } = tokenSlice.actions;

export const getAccessToken = (state: RootState) => state.tokenHandling?.accessToken;
export const getIdToken = (state: RootState) => state.tokenHandling.idToken;
export const getTenant = (state: RootState) => state.tokenHandling.idToken?.tenant as Tenant;

export const isSouthKoreaTenantValue = (tenant?: Tenant) =>
    tenant === TENANT_RIO_SOUTHKOREA_PROD || tenant === TENANT_RIO_SOUTHKOREA_TEST;

export const isSouthAfricaTenantValue = (tenant?: Tenant) =>
    tenant === TENANT_RIO_SOUTHAFRICA_PROD || tenant === TENANT_RIO_SOUTHAFRICA_TEST;

export const isLatamTenantValue = (tenant?: Tenant) =>
    tenant === TENANT_RIO_BRAZIL_TEST || tenant === TENANT_RIO_BRAZIL_PROD;

export const isEuTenantValue = (tenant?: Tenant) => tenant === TENANT_RIO_EU_PROD || tenant === TENANT_RIO_EU_TEST;

export const isLatamTenant = (state: RootState) => {
    const tenant = getTenant(state);

    return isLatamTenantValue(tenant);
};

export const isEuTenant = (state: RootState) => {
    const tenant = getTenant(state);

    return isEuTenantValue(tenant);
};

export default tokenSlice.reducer;
